"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type TariffRateRow = {
  id: string
  name: string
  lineCategory: string
  ratePerTonKm: number
  currency: string
  validFrom: string
  validTo: string | null
  providerType: string
  infrastructureManager: string | null
  isActive: boolean
  createdAt: string
}

type TariffRatesResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapRow(item: Record<string, unknown>): TariffRateRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    name: typeof item.name === 'string' ? item.name : '',
    lineCategory: typeof item.line_category === 'string' ? item.line_category : '',
    ratePerTonKm: typeof item.rate_per_ton_km === 'number' ? item.rate_per_ton_km : 0,
    currency: typeof item.currency === 'string' ? item.currency : 'PLN',
    validFrom: typeof item.valid_from === 'string' ? item.valid_from : '',
    validTo: typeof item.valid_to === 'string' ? item.valid_to : null,
    providerType: typeof item.provider_type === 'string' ? item.provider_type : '',
    infrastructureManager: typeof item.infrastructure_manager === 'string' ? item.infrastructure_manager : null,
    isActive: typeof item.is_active === 'boolean' ? item.is_active : true,
    createdAt: typeof item.created_at === 'string' ? item.created_at : '',
  }
}

const PROVIDER_LABELS: Record<string, string> = {
  PLK: 'PLK (National)',
  PRIVATE: 'Private',
}

const CATEGORY_LABELS: Record<string, string> = {
  TRUNK: 'Trunk',
  MAIN: 'Main',
  SECONDARY: 'Secondary',
  LOCAL: 'Local',
}

export default function TariffRatesPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'route-planning-list',
  })
  const [rows, setRows] = React.useState<TariffRateRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'lineCategory',
      label: t('tariff_rates.filters.lineCategory', 'Line Category'),
      type: 'select' as const,
      options: Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
    },
    {
      id: 'providerType',
      label: t('tariff_rates.filters.providerType', 'Provider'),
      type: 'select' as const,
      options: Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label })),
    },
  ], [t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const lineCategory = filterValues.lineCategory
    if (typeof lineCategory === 'string' && lineCategory.trim()) params.set('lineCategory', lineCategory)
    const providerType = filterValues.providerType
    if (typeof providerType === 'string' && providerType.trim()) params.set('providerType', providerType)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: TariffRatesResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<TariffRatesResponse>(
          `/api/tariff_rates/tariff-rates?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('tariff_rates.error.load', 'Failed to load tariff rates')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapRow).filter((row): row is TariffRateRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('tariff_rates.error.load', 'Failed to load tariff rates')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (rate: TariffRateRow) => {
    if (!rate.id) return
    const confirmed = await confirm({
      title: t('tariff_rates.deleteConfirm', 'Delete tariff rate "{{name}}"?', { name: rate.name }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/tariff_rates/tariff-rates?id=${encodeURIComponent(rate.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('tariff_rates.deleteError', 'Failed to delete tariff rate') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== rate.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('tariff_rates.deleteSuccess', 'Tariff rate deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('tariff_rates.deleteError', 'Failed to delete tariff rate')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<TariffRateRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'name',
        header: t('tariff_rates.columns.name', 'Name'),
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'lineCategory',
        header: t('tariff_rates.columns.lineCategory', 'Line Category'),
        cell: ({ row }) => CATEGORY_LABELS[row.original.lineCategory] ?? row.original.lineCategory,
      },
      {
        accessorKey: 'ratePerTonKm',
        header: t('tariff_rates.columns.ratePerTonKm', 'Rate/t-km'),
        cell: ({ row }) => (
          <span className="font-mono">
            {row.original.ratePerTonKm.toFixed(4)} {row.original.currency}
          </span>
        ),
      },
      {
        accessorKey: 'providerType',
        header: t('tariff_rates.columns.providerType', 'Provider'),
        cell: ({ row }) => PROVIDER_LABELS[row.original.providerType] ?? row.original.providerType,
      },
      {
        accessorKey: 'validFrom',
        header: t('tariff_rates.columns.validFrom', 'Valid From'),
        cell: ({ row }) => row.original.validFrom || noValue,
      },
      {
        accessorKey: 'validTo',
        header: t('tariff_rates.columns.validTo', 'Valid To'),
        cell: ({ row }) => row.original.validTo ?? noValue,
      },
      {
        accessorKey: 'isActive',
        header: t('tariff_rates.columns.isActive', 'Active'),
        cell: ({ row }) => (
          <span className={row.original.isActive ? 'text-green-600' : 'text-muted-foreground'}>
            {row.original.isActive
              ? t('tariff_rates.active', 'Active')
              : t('tariff_rates.inactive', 'Inactive')}
          </span>
        ),
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<TariffRateRow>
          title={t('tariff_rates.title', 'Tariff Rates')}
          refreshButton={{
            label: t('tariff_rates.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/tariff-rates/new">
                {t('tariff_rates.actions.create', 'New Tariff Rate')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('tariff_rates.searchPlaceholder', 'Search by name...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'tariff_rates.list' }}
          onRowClick={(row) => router.push(`/backend/tariff-rates/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('tariff_rates.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
